// @ts-strict-ignore
import { initialize as initializeIAMV2 } from "@braze/appboy-web-sdk-iam-v2";
import type { ContentCards, FeatureFlag } from "@braze/web-sdk-develop";
import {
  setLogger,
  initialize,
  showInAppMessage,
  subscribeToInAppMessage,
  subscribeToFeatureFlagsUpdates,
  subscribeToContentCardsUpdates,
  requestContentCardsRefresh,
  openSession,
} from "@braze/web-sdk-develop";

import { StackOrder } from "@lib/canvasStackOrder";

import type { InitBrazeAtBrazeProps } from "./types";

const initialized = { current: false };

const listeners = { current: new Set<(cards: ContentCards) => void>() };

const featureFlagListeners = { current: new Set<(featureFlags: FeatureFlag[]) => void>() };

/**
 * Initialize Braze@Braze. This is used in both dashboard-require-init and in useBrazeAtBrazeNewsFeed()
 *
 * This only sends the user registration once, but can be called many times to subscribe to content cards
 * for the `Braze@Braze` news feed
 * @returns a subscription string for content card updates
 */
export const initBrazeAtBraze = ({
  logger,
  webSdkBackend,
  safariPushWebsiteId,
  webSdkApiKey,
  sendDeveloperProfileToBrazeWebSdk,
  headless = false,
}: InitBrazeAtBrazeProps): string => {
  if (initialized.current) {
    // prevent double-init
    return;
  }
  try {
    setLogger((message: string) => {
      if (message.indexOf("Braze SDK Error") === 0) {
        logger.info(message);
      }
    });

    // Initialize Web SDK versions from oldest to newest so any conflicting styles take their latest form.
    //
    // The older webSdkForIAMV2 is initialized with an invalid API Key so it doesn't accidentally get to flush any
    // pending events. We only use it to render locally created messages.
    initializeIAMV2("invalid_api_key", {
      baseUrl: webSdkBackend,
      safariWebsitePushId: safariPushWebsiteId,
      doNotLoadFontAwesome: true,
      openNewsFeedCardsInNewTab: true,
      enableHtmlInAppMessages: true,
      sessionTimeoutInSeconds: 300,
    });

    // In the headless Operator runtime no human is present, so we deliberately stop here and never
    // start the live braze@braze session below: no openSession, no in-app-message / content-card /
    // feature-flag subscriptions, and no developer-profile registration. This ensures a headless run
    // neither receives any braze@braze messaging (in-app messages, content cards, product
    // announcements, product tours, sales CTAs — all driven by this session and its delivered
    // feature flags) nor logs impressions/sessions that would make it look like a real user was
    // reached. The invalid-key IAMV2 renderer above is intentionally kept: it opens no session and
    // emits nothing, and Operator may still need it to render a preview of an in-app message it is
    // authoring.
    if (headless) {
      // prevent double-init
      initialized.current = true;
      return;
    }

    initialize(webSdkApiKey, {
      baseUrl: webSdkBackend,
      safariWebsitePushId: safariPushWebsiteId,
      enableLogging: true,
      doNotLoadFontAwesome: true,
      openCardsInNewTab: true,
      allowUserSuppliedJavascript: true,
      sessionTimeoutInSeconds: 300,
      inAppMessageZIndex: StackOrder.BRAZE_AT_BRAZE_IAM,
    });

    subscribeToInAppMessage((message) => {
      // don't show braze@braze IAM if native dashboard modal is open
      if (document.querySelector(".bcl-modal-backdrop")) {
        return;
      }
      showInAppMessage(message);
    });

    sendDeveloperProfileToBrazeWebSdk();

    subscribeToContentCardsUpdates((cards) => {
      for (const listener of listeners.current) {
        listener(cards);
      }
    });

    enableFeatureFlagSubscriptions();

    openSession();

    // prevent double-init
    initialized.current = true;
  } catch (e) {
    logger.error(e);
  }
};

const lastFeatureFlags = { current: [] as FeatureFlag[] };

// extracting this so that we can use it in storybook mocking
export const enableFeatureFlagSubscriptions = () => {
  subscribeToFeatureFlagsUpdates((featureFlags) => {
    lastFeatureFlags.current = featureFlags;
    for (const listener of featureFlagListeners.current) {
      listener(featureFlags);
    }
  });
};

export const addContentCardListener = (listener: (cards: ContentCards) => void) => {
  listeners.current.add(listener);
  if (initialized.current) {
    // make sure the new listener has up-to-date content cards
    requestContentCardsRefresh();
  }
};

export const removeContentCardListener = (listener: (cards: ContentCards) => void) => {
  listeners.current.delete(listener);
};

export const addFeatureFlagListener = (listener: (featureFlags: FeatureFlag[]) => void) => {
  featureFlagListeners.current.add(listener);
  if (lastFeatureFlags.current.length) {
    // pass any existing flags into the listener
    listener(lastFeatureFlags.current);
  }
};

export const removeFeatureFlagListener = (listener: (featureFlags: FeatureFlag[]) => void) => {
  featureFlagListeners.current.delete(listener);
};
